import * as React from 'react'
import Image from 'next/image'

import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'

import HeaderBar, { HeaderBarHeightPixel } from 'components/atomic/organisms/header'
import ResponsiveContainer from 'components/atomic/organisms/responsive-container'
import SEO, { Breadcrumb } from 'components/atomic/atoms/simple-page-seo'
import HeaderOnlyLogo from 'components/atomic/organisms/header-only-logo'
import Footer from 'components/atomic/organisms/footer'
import Card from 'components/atomic/atoms/card'

interface ICardLayout {
    title?: string
    description?: string
    breadcrumbs?: Breadcrumb[]
    children: React.ReactElement | React.ReactElement[]
    showHeader?: boolean
    showFooter?: boolean
    headerType?: 'default' | 'only-logo' | 'only-logo-lock'
}

const Background = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    opacity: 0.3;
    height: 100%;
    z-index: 0;

    ${({ theme }) => theme.breakpoints.down('sm')} {
        display: none;
    }
`

const Wrapper = styled('div')`
    margin: auto;
    padding-top: 1rem;

    ${({ theme }) => theme.breakpoints.up('sm')} {
        padding-top: 6.5rem;
        min-width: 6rem;
        width: 28rem;
    }
`

const CardLayout = ({
    children,
    headerType,
    showFooter = false,
    showHeader = true,
    title,
    description,
    breadcrumbs,
    ...props
}: ICardLayout) => {
    const marginTop = React.useMemo(() => (showHeader ? HeaderBarHeightPixel : 0), [showHeader])

    const Header = React.useMemo(() => {
        if (!showHeader) return null
        if (headerType === 'only-logo') return <HeaderOnlyLogo />
        if (headerType === 'only-logo-lock') return <HeaderOnlyLogo clickableLogo={false} />
        return <HeaderBar />
    }, [showHeader, headerType])

    return (
        <>
            {title && <SEO title={title} description={description} breadcrumbs={breadcrumbs} />}

            {showHeader ? Header : null}

            <ResponsiveContainer maxWidth="xl" {...props}>
                <Background>
                    <Image alt="" layout="fill" src={'/assets/lines-background.png'} />
                </Background>

                <Wrapper style={{ marginTop: `${marginTop}px`, marginBottom: '8rem' }}>
                    {/** Mobile */}
                    <Box sx={{ display: { sm: 'none' } }}>{children}</Box>
                    {/** Desktop */}
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Card>{children}</Card>
                    </Box>
                </Wrapper>
            </ResponsiveContainer>

            {showFooter ? <Footer /> : null}
        </>
    )
}

export default CardLayout
